/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ArrayProxy from '@ember/array/proxy';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import CampaignModel, { EventTriggeredCampaignAttributeFilters } from 'cio/models/campaign';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import LogModel from 'cio/models/log';
import { queryEncode } from 'cio/helpers/query';
import { serializeFilters } from 'cio/utils/conditions/serialize/event';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import DS from 'ember-data';

const DEBOUNCE_DELAY = config.APP.shortDelay;

export interface EventArgs {
  attributeFilters: EventTriggeredCampaignAttributeFilters;
  campaign: CampaignModel;
  event: string;
  filters: Record<string, unknown>[];
  toggleSidebar: () => unknown;
  updateTopLevelValues: <K extends 'event' | 'attributeFilters', V extends EventArgs[K]>(
    values: Record<K, V>,
  ) => unknown;
}

export default class Event extends Component<EventArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  @tracked eventLogs: LogModel[] | ArrayProxy<LogModel> = [];
  @tracked filterMatches: string[] = [];
  @tracked updateFiltersOnly = false;

  // will be used to track locally changed values, before hitting main "Save" in the sidebar
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked event: EventArgs['event'] = this.args.event;
  @tracked changedAttributeFilters: EventArgs['attributeFilters'] | null = null;

  constructor(owner: unknown, args: EventArgs) {
    super(owner, args);

    if (!isEmpty(this.args.event)) {
      this.fetchLogs.perform();
    }
  }

  setDefaults() {
    this.eventLogs = [];
    this.filterMatches = [];
    this.updateFiltersOnly = false;
  }

  fetchLogs = task({ restartable: true }, async () => {
    await timeout(DEBOUNCE_DELAY);

    let { attributeFilters, event } = this;

    if (isEmpty(event)) {
      this.setDefaults();
      return;
    }

    let query = {
      type: 'event',
      name: event,
      since: true,
      filters: '',
    };

    if (!(isNone(attributeFilters) || attributeFilters.hasErrors)) {
      let serialized = serializeFilters(attributeFilters.children, attributeFilters.operator, true, true);
      query.filters = queryEncode(serialized);
    }

    try {
      let result = await this.store.query('log', query);

      this.eventLogs = result;
      this.filterMatches = (result as any).meta.filter_matches;
      this.updateFiltersOnly = false;
    } catch (e) {
      this.setDefaults();
    }
  });

  get filteredEvents() {
    return this.eventLogs.filter((log) => this.filterMatches.includes(log.id));
  }

  get attributeFilters() {
    // first check for locally changed attributeFilters
    if (this.changedAttributeFilters != null) {
      return this.changedAttributeFilters;
    }
    // if no changes are made yet, then return from args
    return this.args.attributeFilters;
  }

  get hasChanges() {
    return this.args.event !== this.event || this.changedAttributeFilters != null;
  }

  @action
  onAddFilter(name = '', value = '') {
    this.updateFiltersOnly = true;
    let clone = this.attributeFilters.duplicate();
    clone.children = [
      ...clone.children,
      EventAttributeCondition.create({
        event: this.args.event,
        name,
        operator: 'eq',
        value,
        shouldValidate: false,
      }),
    ];
    this.changedAttributeFilters = clone as EventTriggeredCampaignAttributeFilters;
  }

  @action
  onRemoveFilter(_filter: EventAttributeCondition, index: number) {
    this.updateFiltersOnly = true;
    let clone = this.attributeFilters.duplicate();
    clone.children.splice(index, 1); // .duplicate does create new "children" array ref
    this.changedAttributeFilters = clone as EventTriggeredCampaignAttributeFilters;
    this.fetchLogs.perform();
  }

  @action
  onEventChange(event: string) {
    let clone = this.attributeFilters.duplicate();
    clone.children = clone.children.map((filter: EventAttributeCondition) => {
      let clone = filter.duplicate();
      clone.event = event;
      return clone;
    });
    this.changedAttributeFilters = clone as EventTriggeredCampaignAttributeFilters;
    this.fetchLogs.perform();
  }

  @action
  onConditionChange() {
    this.updateFiltersOnly = true;
    this.fetchLogs.perform();
  }

  @action
  saveAndClose() {
    this.args.updateTopLevelValues({
      event: this.event,
      attributeFilters: this.attributeFilters,
    });
    this.close();
  }

  @action close() {
    this.args.toggleSidebar();
  }
}
